/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect } from "react"
import { observer } from "mobx-react-lite"
import { Button } from "@mui/material"
import { addMinutes } from "date-fns"
import Loading from "@medaica/common/components/loading"
import { useBookingStores } from "../booking-context"
import BookingLayout from "../components/booking-layout"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"
import { usTimeZoneLabel, usTimeZoneShortLabel } from "../timezones"

const copy = bookingCopy.done

const fmt = (date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat("en-US", { ...options, timeZone }).format(date)

// e.g. "Friday, July 24, 2026" — full date incl. year, no time. The exam window renders on its own
// line via formatWindow; the picked timezone label on the line below that.
const formatDate = (slotStart: Date, timeZone: string): string =>
  fmt(slotStart, timeZone, { weekday: "long", month: "long", day: "numeric", year: "numeric" })

// Split a rendered time into its clock ("10:30") and period ("AM") so a start–end range can share a
// single trailing period when both fall in the same half-day. formatToParts keeps this deterministic
// across ICU versions (jsdom included), unlike DateTimeFormat.formatRange's locale-dependent dash.
const clockParts = (date: Date, timeZone: string): { time: string; period: string } => {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).formatToParts(date)
  const period = parts.find((part) => part.type === "dayPeriod")?.value ?? ""
  const time = parts
    .filter((part) => part.type === "hour" || part.type === "minute" || part.value === ":")
    .map((part) => part.value)
    .join("")
  return { time, period }
}

// e.g. "10:30–11:00 AM Pacific Time" — the exam window (start + examMinutes) plus the short tz name.
// examMinutes comes from the booking config; if it isn't loaded, fall back to the start time alone so
// this never renders an "Invalid Date" range.
const formatWindow = (slotStart: Date, examMinutes: number | null, timeZone: string): string => {
  const start = clockParts(slotStart, timeZone)
  const shortZone = usTimeZoneShortLabel(timeZone)
  if (examMinutes == null) {
    return `${start.time} ${start.period} ${shortZone}`
  }
  const end = clockParts(addMinutes(slotStart, examMinutes), timeZone)
  const range =
    start.period === end.period
      ? `${start.time}–${end.time} ${end.period}`
      : `${start.time} ${start.period}–${end.time} ${end.period}`
  return `${range} ${shortZone}`
}

const Done = observer((): ReactElement => {
  const { sessionStore, uiStore, wizardStore } = useBookingStores()
  const result = wizardStore.bookingResult
  const patient = wizardStore.patient

  useEffect(() => {
    if (!result || !patient) {
      uiStore.goTo("account")
    }
  }, [result, patient, uiStore])

  const finishTo = (view: "patient" | "account"): void => {
    // Navigate first so Done unmounts before reset() nulls the result (which would
    // otherwise race the guard effect into "account"); loadAccount is expiry-safe
    // internally now, so refreshing in the background can't leak a rejection.
    uiStore.goTo(view)
    wizardStore.reset()
    void sessionStore.loadAccount()
  }

  if (!result || !patient) {
    return (
      <BookingLayout>
        <div className="py-24">
          <Loading />
        </div>
      </BookingLayout>
    )
  }

  return (
    <BookingLayout headerRight={sessionStore.account?.buyerFirstName ?? ""}>
      <div className="max-w-lg mx-auto mt-12 px-6">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center text-3xl"
            style={{ backgroundColor: bookingColors.successBackground, color: bookingColors.successForeground }}
          >
            ✓
          </div>
          <p className="mt-4 text-gray-600">{copy.scheduledFor}</p>
          <h1 className="mt-1 text-2xl font-bold" style={{ color: bookingColors.primary }}>
            {formatDate(result.slotStart, result.timeZone)}
          </h1>
          <p className="mt-1 text-lg font-semibold text-gray-800">
            {formatWindow(result.slotStart, sessionStore.config?.examMinutes ?? null, result.timeZone)}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {copy.selectedTimeZone} {usTimeZoneLabel(result.timeZone)}
          </p>

          <p className="mt-6 text-sm font-medium text-gray-700">
            {copy.confirmationId}: {result.examRef}
          </p>

          {/* Help notes: left-aligned divided list under the centered facts above. border-t draws
              the line under the Confirmation ID; divide-y the lines between the three notes. */}
          <div className="mt-4 text-left text-sm text-gray-600 border-t border-gray-100 divide-y divide-gray-100">
            <p className="py-3">
              {copy.joinNoteBefore}
              <span className="font-semibold text-gray-800">{patient.email}</span>
              {copy.joinNoteAfter}
            </p>

            <p className="py-3">
              {copy.faqPrompt}{" "}
              <a
                href={copy.faqUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: bookingColors.primary }}
              >
                {copy.faqLinkText}
              </a>
              {"."}
            </p>

            <p className="py-3">
              {copy.rescheduleBefore}
              <a
                href={`mailto:${bookingCopy.supportEmail}`}
                className="underline"
                style={{ color: bookingColors.primary }}
              >
                {bookingCopy.supportEmail}
              </a>
              {copy.rescheduleAfter}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {result.examsRemaining > 0 ? (
            <Button fullWidth variant="contained" onClick={() => finishTo("patient")}>
              {copy.bookAnother(result.examsRemaining)}
            </Button>
          ) : null}
          <Button fullWidth variant="outlined" color="inherit" onClick={() => finishTo("account")}>
            {copy.viewMyExams}
          </Button>
        </div>
      </div>
    </BookingLayout>
  )
})

export default Done
